import React, { FC, useCallback, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { useIntl } from 'react-intl';
import { connect } from 'react-redux';
import { Link, NavLink, useLocation } from 'react-router-dom';
import {
  Collapse,
  Nav,
  Navbar,
  NavbarBrand,
  NavbarToggler,
  NavItem,
} from 'reactstrap';
import { ReduxState } from 'types';
import logoIcon from '../../assets/img/brand/logo_icon_reverse_2026.svg';
import logoWithName from '../../assets/img/brand/logo_reverse_2026.svg';
import * as consts from '../../consts/consts';
import { FLAGS } from '../../consts/consts';
import { useLocalStorageState, useWindowResize } from '../../utils/util/hooks';
import { isEnabled } from '../../utils/util/util';
import PeopleNav from './PeopleNav';
import SiteNav from './SiteNav';

interface Props
  extends Pick<
    ReduxState,
    'currentOrganization' | 'currentProxyPerson' | 'me' | 'features'
  > {
  showIfEnabled: (flag: string, element: any) => any;
  isSuperAdmin: boolean;
  isOrgSystemAdmin: boolean;
}

const LEFT_NAV_COLLAPSED_STORAGE_KEY = 'left-nav-collapsed';

// Dashkit's `navbar-expand-md`: from this width the nav is the vertical
// sidebar (which can be collapsed to a rail); below it, a horizontal bar
// with a hamburger, where the collapsed preference does not apply
const LEFT_NAV_RAIL_MIN_WIDTH_PX = 768;

const MainLeftNav: FC<Props> = (props) => {
  const { formatMessage } = useIntl();

  // mobile (< md): the navbar is a horizontal bar with a hamburger toggler
  const [isOpen, setIsOpen] = useState(false);

  // desktop (>= md): the navbar can be collapsed to an icon-only rail;
  // the preference is remembered per user in localStorage
  const [isCollapsedPreference = false, setIsCollapsedPreference] =
    useLocalStorageState<boolean>(LEFT_NAV_COLLAPSED_STORAGE_KEY, false);

  // the preference is ignored (but kept) on the mobile layout
  const windowWidth = useWindowResize();
  const isCollapsed =
    windowWidth >= LEFT_NAV_RAIL_MIN_WIDTH_PX && isCollapsedPreference;

  const toggleNavbar = () => setIsOpen(!isOpen);

  const toggleCollapsed = useCallback(
    () => setIsCollapsedPreference((prev) => !prev),
    [setIsCollapsedPreference]
  );

  const referralsEnabled = isEnabled(props.features, FLAGS.REFERRALS);
  const referralsLabel = formatMessage({
    id: 'app.views.layout.mainleftnav.referrals',
    defaultMessage: 'Refer Confirm',
  });

  // everything in the markup that depends on the rail being collapsed
  const layout = isCollapsed
    ? {
        navbarClassName:
          'fixed-start navbar-vertical navbar-dark left-navbar navbar-vertical-sm left-navbar-collapsed',
        brandLogo: {
          src: logoIcon,
          style: { maxHeight: '2rem', maxWidth: '2rem' },
          className: 'nav-link navbar-brand pt-0 pb-0 pt-md-4 pb-md-4',
        },
        // icon-only links get their label as a tooltip
        referralsTitle: referralsLabel,
        toggle: {
          className:
            'left-navbar-collapse-toggle left-navbar-collapse-toggle--collapsed',
          label: formatMessage({
            id: 'app.views.layout.mainleftnav.expand_nav',
            defaultMessage: 'Expand navigation',
          }),
          icon: 'fe fe-chevrons-right',
          expanded: false,
        },
      }
    : {
        navbarClassName: 'fixed-start navbar-vertical navbar-dark left-navbar',
        brandLogo: {
          src: logoWithName,
          style: { maxHeight: '3.5rem', maxWidth: '7.5rem' },
          className: 'nav-link navbar-brand pt-0 pb-0 pt-md-4 pb-md-5',
        },
        referralsTitle: undefined,
        toggle: {
          className: 'left-navbar-collapse-toggle',
          label: formatMessage({
            id: 'app.views.layout.mainleftnav.collapse_nav',
            defaultMessage: 'Collapse navigation',
          }),
          icon: 'fe fe-chevrons-left',
          expanded: true,
        },
      };

  // if the page changes, since we scroll to top, we need to close the navbar
  // if it is open to avoid annoying the user having to keep scrolling
  // down when they change tabs on a given page
  const { pathname } = useLocation();
  useEffect(() => {
    setIsOpen(false);
  }, [pathname]);

  return (
    <Navbar
      container={false}
      expand="md"
      className={layout.navbarClassName}
      data-testid="main-left-nav"
    >
      <div className="container-fluid">
        <NavbarToggler onClick={toggleNavbar} />
        <div className="pe-sm-3 nav-item">
          <NavbarBrand tag={Link} to="/" className={layout.brandLogo.className}>
            <img
              style={layout.brandLogo.style}
              src={layout.brandLogo.src}
              alt="Confirm Logo"
            />
          </NavbarBrand>
        </div>
        {props.currentOrganization?.id && (
          <>
            <Collapse isOpen={isOpen} navbar>
              <SiteNav
                showIfEnabled={props.showIfEnabled}
                isSuperAdmin={props.isSuperAdmin}
                isOrgSystemAdmin={props.isOrgSystemAdmin}
                isCollapsed={isCollapsed}
              />
              <hr className="navbar-divider my-3"></hr>
              <PeopleNav
                inNav={true}
                showSelf={true}
                isCollapsed={isCollapsed}
              />
              {referralsEnabled && (
                <Nav navbar className="mt-auto">
                  <NavItem>
                    <NavLink
                      className="nav-link"
                      to={'/referrals'}
                      title={layout.referralsTitle}
                    >
                      <i className={consts.ICONS.REFERRAL}></i>
                      <span className="left-navbar-label">
                        {referralsLabel}
                      </span>
                    </NavLink>
                  </NavItem>
                </Nav>
              )}
            </Collapse>
            {/* Collapse handle: a tab flush against the nav's right edge
                just below the logo (per design review on CONF-237). It is
                portaled to <body> so the fixed navbar's scrolling and
                stacking context don't affect it. Hidden on small screens
                (see _custom.scss), where the nav is a horizontal bar with
                a hamburger instead. */}
            {createPortal(
              <button
                type="button"
                // deliberately not a .btn/.btn-link, and with double-chevron
                // icons: E2E tests select .btn-link by index and buttons by
                // their single-chevron icon (e.g. previous/next quarter)
                className={layout.toggle.className}
                onClick={toggleCollapsed}
                title={layout.toggle.label}
                aria-label={layout.toggle.label}
                aria-expanded={layout.toggle.expanded}
              >
                <i className={layout.toggle.icon}></i>
              </button>,
              document.body
            )}
          </>
        )}
      </div>
    </Navbar>
  );
};

const mapStateToProps = (
  state: ReduxState
): Pick<
  ReduxState,
  'currentOrganization' | 'currentProxyPerson' | 'me' | 'features'
> => {
  const { currentOrganization, currentProxyPerson, me, features } = state;

  return {
    currentOrganization,
    currentProxyPerson,
    me,
    features,
  };
};

// all tracking in app will be passed through here
export default connect(mapStateToProps)(React.memo(MainLeftNav));
